import { styled } from "@mui/material";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridSortModel,
} from "@mui/x-data-grid-pro";
import { format, parseISO } from "date-fns";
import { type CommercialMonitoringResultRow } from "../types";
import { DataGrid } from "./DataGrid";
import { type NavigateFunction } from "react-router-dom";
import { useState } from "react";
import {
  type ToolbarProps,
  CommercialMonitoringDataGridToolbar,
} from "./CommercialMonitoringDataGridToolbar";
import { useGridPagination } from "../hooks/useGridPagination";
import { commercialMonitoringDataGridStyles } from "./commercialMonitoringDataGridStyles";
import { CommercialMonitoringActionCell } from "./CommercialMonitoringActionCell";

interface IProps {
  navigate: NavigateFunction;
  rows: CommercialMonitoringResultRow[];
  handleClickAcquireCommercialBook: (
    row: CommercialMonitoringResultRow
  ) => void;
}

const StyledDataGrid = styled(DataGrid)`
  ${commercialMonitoringDataGridStyles}
`;

const makeGridColDef = (
  navigate: NavigateFunction,
  handleClickAcquireCommercialBook: (row: CommercialMonitoringResultRow) => void
): GridColDef[] => {
  return [
    {
      field: "commercialRegistrationNumber",
      headerName: "会社法人等番号",
      width: 160,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "corporateName",
      headerName: "法人名",
      width: 220,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "legalAffairsBureauName",
      headerName: "法務局",
      width: 140,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "registrationDate",
      headerName: "登録日",
      width: 120,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        if (value != null) return format(parseISO(value), "yyyy/MM/dd");
      },
    },
    {
      field: "remarks",
      headerName: "備考",
      width: 200,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "requestDate",
      headerName: "法務局受付日",
      width: 120,
      sortable: true,
      sortingOrder: ["desc", "asc"],
      align: "left",
      headerAlign: "left",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        if (value != null) return format(parseISO(value), "yyyy/MM/dd");
      },
    },
    {
      field: "receptionReason",
      headerName: "登記原因",
      width: 200,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 300,
      sortable: false,
      align: "left",
      headerAlign: "left",
      disableExport: true,
      renderCell: ({
        row,
      }: GridRenderCellParams<string, CommercialMonitoringResultRow>) => {
        return (
          <CommercialMonitoringActionCell
            row={row}
            navigate={navigate}
            onClickAcquireCommercialBook={handleClickAcquireCommercialBook}
          />
        );
      },
    },
  ];
};

/**
 * モニタリング結果の表部分（監視対象＝法人。チェックボックス選択は持たない）
 * @param {IProps} props
 * @returns
 */
export const CommercialMonitoringResultDataGrid: React.FC<IProps> = (
  props: IProps
) => {
  const { navigate, rows, handleClickAcquireCommercialBook } = props;
  const colDef = makeGridColDef(navigate, handleClickAcquireCommercialBook);
  const { page, pageSize, changePage, changePageSize } = useGridPagination();
  const rowsPerPageOptions: number[] = [10, 50, 100];

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const toolbarProps: ToolbarProps = {
    page,
    pageSize,
    rowsPerPageOptions,
    changePage,
    changePageSize,
    allCount: rows.length,
  };

  return (
    <StyledDataGrid
      autoHeight
      columns={colDef}
      disableColumnMenu
      disableSelectionOnClick={true}
      rows={rows}
      pageSize={pageSize}
      rowCount={rows.length}
      page={page}
      rowsPerPageOptions={rowsPerPageOptions}
      onPageChange={changePage}
      onPageSizeChange={changePageSize}
      sortModel={sortModel}
      onSortModelChange={(model) => {
        setSortModel(model);
      }}
      components={{
        Toolbar: CommercialMonitoringDataGridToolbar,
      }}
      componentsProps={{
        toolbar: {
          ...toolbarProps,
        },
      }}
    />
  );
};
